import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { useBranding } from "@/context/BrandingContext";
import { api } from "@/lib/api";
import { Compass, ShieldCheck, LogIn, Info } from "lucide-react";

/**
 * M1-16 · Simplified internal landing page.
 * Dream Home ERP is an internal-only platform. No public marketing, no self-registration.
 */
export default function Landing() {
  const { branding, version } = useBranding();
  const companyName = branding?.company_name || "Dream Home ERP";
  const legalName = branding?.legal_name || "Dream Home Engineers Pvt Ltd";
  const { data: build } = useQuery({
    queryKey: ["build-info"],
    queryFn: async () => (await api.get("/platform/build-info")).data,
    staleTime: 5 * 60_000,
  });
  const envLabel = build?.environment === "production" ? "PROD"
                 : build?.environment === "preview" ? "PREVIEW"
                 : build?.environment === "development" ? "DEV" : "";
  const envStyle = build?.environment === "production" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                 : "bg-amber-50 text-amber-700 border-amber-200";
  const deployedAt = build?.deployed_at
    ? new Date(build.deployed_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : "";

  return (
    <div className="min-h-screen bg-white text-zinc-900 flex flex-col" data-testid="landing-page">
      <header className="border-b border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="landing-logo">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="logo" className="w-8 h-8 rounded-md object-contain" />
            ) : (
              <div className="w-8 h-8 rounded-md bg-zinc-900 text-white grid place-items-center">
                <Compass className="w-4 h-4" />
              </div>
            )}
            <div className="leading-tight">
              <div className="text-[13px] font-heading font-bold tracking-tight" data-testid="landing-brand-name">{companyName}</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Business Operating System</div>
            </div>
          </div>
          <Button asChild size="sm" data-testid="landing-header-login">
            <Link to="/login"><LogIn className="w-3.5 h-3.5 mr-1.5" /> Employee Login</Link>
          </Button>
        </div>
      </header>

      <main className="flex-1 flex items-center">
        <div className="max-w-3xl mx-auto px-8 py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-[11px] font-medium mb-8" data-testid="landing-secure-badge">
            <ShieldCheck className="w-3 h-3" />
            <span>Authorized users only · Internal platform</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight" data-testid="landing-title">
            {companyName}
          </h1>
          <div className="mt-3 text-sm sm:text-base text-zinc-600" data-testid="landing-legal-name">{legalName}</div>

          <p className="mt-8 text-base sm:text-lg text-zinc-600 max-w-xl mx-auto" data-testid="landing-tagline">
            Secure internal business management platform for authorized users.
          </p>

          <div className="mt-10 flex items-center justify-center">
            <Button asChild size="lg" className="min-w-56" data-testid="landing-enter-btn">
              <Link to="/login">
                Enter Platform
                <LogIn className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>

          <div className="mt-12 text-[11px] text-zinc-400 space-y-1">
            <div>Employee accounts are provisioned by the Administration team only.</div>
            <div>If you don't have an account, please contact your HR or Managing Director.</div>
          </div>
        </div>
      </main>

      <footer className="border-t border-zinc-200 py-4">
        <div className="max-w-[1400px] mx-auto px-8 flex flex-wrap items-center justify-between gap-2 text-[11px] text-zinc-500">
          <div>© {new Date().getFullYear()} {legalName}. All rights reserved.</div>
          <div className="flex items-center gap-2">
            {envLabel && (
              <span className={`px-1.5 py-0.5 rounded border font-mono text-[10px] ${envStyle}`} data-testid="build-env-pill">{envLabel}</span>
            )}
            <span className="font-mono" data-testid="landing-version">{version?.version || ""}</span>
            <div className="relative group" data-testid="build-info-anchor">
              <button
                type="button"
                className="p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700"
                aria-label="Build info"
                data-testid="build-info-btn"
              >
                <Info className="w-3 h-3" />
              </button>
              <div
                role="tooltip"
                className="pointer-events-none opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity absolute right-0 bottom-full mb-2 w-64 rounded-md border border-zinc-200 bg-white shadow-lg p-3 text-left"
                data-testid="build-info-panel"
              >
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Build Info</div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-[11px]">
                  <div className="text-zinc-500">Environment</div>
                  <div data-testid="build-env" className="text-zinc-900">{build?.environment || "—"}</div>
                  <div className="text-zinc-500">Deployed</div>
                  <div data-testid="build-deployed" className="text-zinc-900">{deployedAt || "—"}</div>
                  <div className="text-zinc-500">Commit</div>
                  <div data-testid="build-sha" className="text-zinc-900">{build?.git_sha || "—"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
